import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { BaseService } from '../base/base.service';
import { SessionUserHashManagerService } from '../session-user-hash-manager/session-user-hash-manager.service';
import {
  BookingAccess, BookingAccessPerson, ClientLinkWholeClient, NamedBookingGrant
} from '../../_models/maintenance/booking-access.model';

/**
 * Typed calls to api/BookingAccess: who can see a booking, naming and removing people on it, the bookings a person
 * is named on, and each client link's Can see choice (WholeClient). The API is staff only, needs the
 * BookingSetupView role feature, and returns 404 while the clientusers.booking-grants switch is off.
 *
 * Follows the shared BaseService pattern: the object passed to prepareOptions() is the exact body sent, GETs pass
 * null (the query string is not part of the hash), and responses are unwrapped from the full response.
 */
@Injectable({ providedIn: 'root' })
export class BookingAccessService extends BaseService {

  private apiUrl: string = environment.apiUrl;

  constructor(private http: HttpClient, protected hashManagerService: SessionUserHashManagerService) {
    super(hashManagerService);
    this.apiUrl += this.apiUrl.endsWith('/') ? '' : '/';
  }

  public getBookingAccess(placementId: number): Observable<BookingAccess> {
    return this.http.get(`${this.apiUrl}api/BookingAccess/${encodeURIComponent(placementId)}`, this.prepareOptions(null)).pipe(
      map((data: any) => this.unwrap(data) as BookingAccess),
      catchError(this.handleError('getBookingAccess'))
    );
  }

  public searchPeople(q: string): Observable<BookingAccessPerson[]> {
    return this.http.get(`${this.apiUrl}api/BookingAccess/People?q=${encodeURIComponent(q ?? '')}`, this.prepareOptions(null)).pipe(
      map((data: any) => (this.unwrap(data) || []) as BookingAccessPerson[]),
      catchError(this.handleError('searchPeople'))
    );
  }

  /** endDate is a yyyy-MM-dd date, or null for no end. */
  public addPerson(placementId: number, userId: number, endDate: string | null): Observable<NamedBookingGrant> {
    const body = { endDate: endDate || null };
    const url = `${this.apiUrl}api/BookingAccess/${encodeURIComponent(placementId)}/People/${encodeURIComponent(userId)}`;
    return this.http.post(url, body, this.prepareOptions(body)).pipe(
      map((data: any) => this.unwrap(data) as NamedBookingGrant),
      catchError(this.handleError('addPerson'))
    );
  }

  public removePerson(placementId: number, userId: number, grantId: number): Observable<NamedBookingGrant> {
    const body = { grantId };
    const url = `${this.apiUrl}api/BookingAccess/${encodeURIComponent(placementId)}/People/${encodeURIComponent(userId)}/Remove`;
    return this.http.post(url, body, this.prepareOptions(body)).pipe(
      map((data: any) => this.unwrap(data) as NamedBookingGrant),
      catchError(this.handleError('removePerson'))
    );
  }

  public getNamedOn(userId: number): Observable<NamedBookingGrant[]> {
    return this.http.get(`${this.apiUrl}api/BookingAccess/Users/${encodeURIComponent(userId)}/NamedOn`, this.prepareOptions(null)).pipe(
      map((data: any) => (this.unwrap(data) || []) as NamedBookingGrant[]),
      catchError(this.handleError('getNamedOn'))
    );
  }

  public getLinks(userId: number): Observable<ClientLinkWholeClient[]> {
    return this.http.get(`${this.apiUrl}api/BookingAccess/Users/${encodeURIComponent(userId)}/Links`, this.prepareOptions(null)).pipe(
      map((data: any) => (this.unwrap(data) || []) as ClientLinkWholeClient[]),
      catchError(this.handleError('getLinks'))
    );
  }

  public setWholeClient(userId: number, clientId: number, wholeClient: boolean): Observable<ClientLinkWholeClient> {
    const body = { wholeClient };
    const url = `${this.apiUrl}api/BookingAccess/Users/${encodeURIComponent(userId)}/Links/${encodeURIComponent(clientId)}/WholeClient`;
    return this.http.post(url, body, this.prepareOptions(body)).pipe(
      map((data: any) => this.unwrap(data) as ClientLinkWholeClient),
      catchError(this.handleError('setWholeClient'))
    );
  }

  /** Shared body unwrap: prepareOptions() observes the full response, not just the body. */
  private unwrap(data: any): any {
    return data ? data.body : data;
  }
}
